import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { News } from '../services/news.service';
import { AngularTranslations } from '../types/angular-primeng-api';
import { UserService } from '../services/open-api/user.service';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CurrentUser } from '../models/user';
import { TranslateInService } from '@/core/services/translate-in.service';
import { environment } from 'src/environments/environment';

/**
 * @class NewsRepository
 * This service provides news.
 * It is like a repository that returns static data.
 * Good enough for now.
 * Later on we can fetch the data from the API or whatever source.
 */
@Injectable({ providedIn: 'root' })
export class NewsRepository {
    public constructor(
        private readonly translate: TranslateService,
        private readonly translateIn: TranslateInService,
        private readonly router: Router,
        public readonly userService: UserService,
    ) {}

    public getNews(): Observable<News[]> {
        const translations$ = this.translate.get([
            'topbar_news_banner',
            'topbar_news_modal_header',
            'topbar_news_modal_content',
            'topbar_news_banner_go',
            'topbar_news_new_integrations',
            'topbar_news_new_integrations_go',
        ]);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore -- WebStorm IDE does not recognize __tb_lang as a global variable, build and runtime are okay.
        const lang = window.__tb_lang!;
        const browserLang = lang.getBrowserLang();
        const translateInLang: string = browserLang || this.translate.getFallbackLang() || lang.defaultLanguage;

        const browserTranslations$ = this.translateIn.get(translateInLang, [
            'topbar_news_language_note',
            'topbar_news_language_note_go',
        ]);

        return combineLatest([translations$, browserTranslations$, this.userService.loadCurrentUser()]).pipe(
            map(
                ([translations, browserTranslations, currentUser]: [
                    AngularTranslations,
                    AngularTranslations,
                    CurrentUser,
                ]) => {
                    const news: News[] = [];

                    // Fixed-content E2E seam. It is unavailable in production builds and cannot
                    // inject arbitrary HTML or application data from the browser.
                    if (
                        environment.e2eTestHooksEnabled &&
                        environment.e2eNewsFixture &&
                        window.__tb_e2e_show_news === true
                    ) {
                        news.push(environment.e2eNewsFixture);
                    }

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const maintenanceNews = {
                        id: 'maintenance-2025-08-30',
                        bannerText: translations['topbar_news_banner'],
                        dialogTitle: translations['topbar_news_modal_header'],
                        dialogContent: translations['topbar_news_modal_content'],
                        actionText: translations['topbar_news_banner_go'],
                    };
                    // news.push(maintenanceNews);

                    if (currentUser.isAdmin()) {
                        const newIntegrationsNews: News = {
                            id: 'new-integrations-2026-08-21',
                            bannerText: translations['topbar_news_new_integrations'],
                            actionText: translations['topbar_news_new_integrations_go'],
                            customAction: () => {
                                void this.router.navigateByUrl('/settings/integrations');
                            },
                        };
                        news.push(newIntegrationsNews);
                    }

                    const userLang = currentUser?.settings?.language;

                    if (browserLang && lang?.isLangSupported?.(browserLang) && userLang !== browserLang) {
                        // Note: The message for a supported language shall be displayed in the browser language, not the standard language.
                        news.push({
                            id: 'lang-supported-' + browserLang,
                            bannerText: browserTranslations['topbar_news_language_note'],
                            actionText: browserTranslations['topbar_news_language_note_go'],
                            customAction: () => {
                                void this.router.navigateByUrl('/profile');
                            },
                        });
                    }

                    return news;
                },
            ),
        );
    }
}
